/**
 *      ▄ █
 *    █ █ █▄▄  LOADBALANCER CLIENT
 *    █ █ ▄ █
 *    ▀     ▀  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../../../../../core/components';
import { BillingApiService } from '../../../http';
import { CheckoutService } from '../../../services/component';
import { Component, Input, OnInit } from '@angular/core';
import { ErrorResponseModel } from '../../../../../../core/models';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LogService, NotifyService } from '../../../../../../core/services';
import { nameValidation } from '../../../../../../core/directives';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-add-billing-contact',
    templateUrl: './add-billing-contact.component.html',
    styleUrls: ['./add-billing-contact.component.scss']
})
export class AddBillingContactComponent extends BaseComponent implements OnInit {
    /**
     * Step index set by parent
     */
    @Input() stepIndex: number;

    /**
     * Active state from the checkout service.
     */
    public activeStep: number;

    /**
     * Page form.
     */
    public formGroup: FormGroup;

    constructor(
        private billingApiService: BillingApiService,
        private checkoutService: CheckoutService,
        private formBuilder: FormBuilder,
        private logService: LogService,
        private notifyService: NotifyService
    ) {
        super();

        this.subscribeToCheckoutService();
        this.setUpForm();
        this.setUpFormSubscriber();

        this.activeStep = this.checkoutService.activeStep;
    }

    ngOnInit(): void {
        this.setUpPage();
    }

    /**
     * Setup form group.
     * @private
     */
    private setUpForm(): void {
        this.formGroup = this.formBuilder.group({
            billingEmail: [null, Validators.required],
            name: [null, Validators.required, nameValidation],
        });
    }

    /**
     * Set up form subscriber.
     * @private
     */
    private setUpFormSubscriber(): void {
        this.formGroup.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    if (this.activeStep === this.stepIndex) {
                        this.checkoutService.isStepValidEmitter.next(this.formGroup.valid);
                    }
                }
            });
    }

    /**
     * Set up page
     * @private
     */
    private setUpPage(): void {
        this.notifyService.muteLocalNotifications();
        this.billingApiService.getAccount()
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    if (data) {
                        this.patchBillingAccount(data);
                    }

                    this.notifyService.unmuteLocalNotifications();
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('PurchaseProductComponent:setUpPage', error.message);
                }
            });
    }

    /**
     * Patch billing account.
     * @param account
     * @private
     */
    private patchBillingAccount(account: any): void {
        this.formGroup.patchValue(account);
    }

    /**
     * Subscribe to check out service.
     * @private
     */
    private subscribeToCheckoutService(): void {
        this.checkoutService.activeStepEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (stepIndex: any): void => {
                    this.activeStep = stepIndex;
                    if (this.activeStep === this.stepIndex) {
                        const timerId = setTimeout((): void => {
                            this.checkoutService.isStepValidEmitter.next(this.formGroup.valid);
                            clearTimeout(timerId);
                        }, 0);
                    }
                }
            });
    }
}
